import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import {toPng, toBlob} from "html-to-image";
import html2canvas from 'html2canvas';
import {elementToSVG, inlineResources} from 'dom-to-svg';
import PptxGenJS from 'pptxgenjs';
import {ActivatedRoute} from '@angular/router';
import {
  trigger,
  state,
  style,
  animate,
  transition,

} from '@angular/animations';
import {List} from '../lists-table/lists-table.component';
import {ListsService} from 'src/app/services/lists/lists.service';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {DragDropModule} from '@angular/cdk/drag-drop';

// Define the animations
export const heightAnimation = trigger('heightAnimation', [
  state('collapsed', style({
    maxHeight: '600px', // Adjust based on your default collapsed height
    overflow: 'hidden',
  })),
  state('expanded', style({
    maxHeight: '1000px', // Adjust based on your desired expanded height
    overflow: 'hidden',
  })),
  transition('collapsed <=> expanded', animate('500ms ease-in-out')),
]);

export const fade = trigger('fade', [
  transition(':enter', [
    style({opacity: 0}),
    animate(300, style({opacity: 1})),
  ]),
]);

export interface ListDetails {
  id: number;
  name: string;
  description: string | null;
  shared_with_count: number;
  creation_date: string; // Changed to string to match ISO format dates
  last_modified: string; // Changed to string to match ISO format dates
  editor_permission: boolean;
  owner: {
    id: number;
    username: string;
    email: string;
  };
}

export interface Retailer {
  name: string;
  irn: string;
  hq_region: {
    name: string;
    code: string;
  };
  sector: string;
  landing_page: string;
  market_cap: string;
  logo: string;
  corporation: {
    name: string;
    ticker: string;
  };
}

export interface ChartType {
  app: string;
  name: string;
  model: string;
  chart_type: string; // Removed 'model' field as it's not used in the response
}

export interface TextElements {
  header: string;
  source: string;
  caption: string;
  subheader: string;
  'og-heading': string;
  'og-description': string;
}

export interface VisualizationDetails {
  id: number;
  name: string;
  chart_type: string;
  custom_chart_id: string;
  custom_id: string;
  custom_chart: string;
  sqa_chart_id: number;
  style: string;
  by_column: string;
  text_elements: TextElements;
  order:number,
}

export interface Visualization {
  id: number;
  name: string;
  chart_type: string;
  rcl_id: number;
  retailer: Retailer | null; // Adjusted to allow for null in case there's no retailer info
  chartType: ChartType;
  visualization: VisualizationDetails[];
  active: boolean;
  created_at: string; // Use string to match ISO format dates
  updated_at: string; // Use string to match ISO format dates
}

export interface VisualizationsResponse {
  list_details: ListDetails;
  visualizations: Visualization[];
}


@Component({
  selector: 'app-saved-charts',
  templateUrl: './saved-charts.component.html',
  styleUrls: ['./saved-charts.component.scss'],
  animations: [heightAnimation, fade], // Include the animations here
})
export class SavedChartsComponent implements OnInit {
  @ViewChild('echelonOwnedLists') echelonOwnedLists!: ElementRef;
  @ViewChild('echelonSharedLists') echelonSharedLists!: ElementRef;
  paramListID: number | null = null;
  showData = false;

  showEchelonOwnedLists = false;
  showEcheclonSharedLists = false;

  ownedListsData: List[] = [];
  sharedListsData: List[] = [];

  listDetails: ListDetails | null = null;
  visualizations: Visualization[] = [];
  displayAddListDialog: boolean = false;
  displayRenameListDialog: boolean = false;
  dragDropActive: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private listService: ListsService,
  ) {
  }

  private mapToList(response: any): List[] {
    return response
      .map((item: any) => ({
        id: item.id,
        list_name: item.list_name,
        shared_with_count: item.shared_with_count,
        creation_date: new Date(item.creation_date),
        last_modified: new Date(item.last_modified),
        charts_count: item.charts_count,
        original_chart_pack: item.original_chart_pack || ' - ',
        editor_permission: item.editor_permission,
        owner: item.owner,
      }))
      .sort(
        (a: List, b: List) =>
          b.last_modified.getTime() - a.last_modified.getTime()
      );
  }

  getAllVisuzlizationsAndShow(listId: number) {
    this.showData = false;
    this.listService.getAllVisualizationsInList(listId).subscribe(
      (response: VisualizationsResponse) => {
        this.listDetails = response.list_details; // Store list details
        this.visualizations = response.visualizations; // Store visualization


        this.showData = true;
      },
      (error: any) => {
        console.error('Error getting list by id', error.error.error);
      }
    );
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.paramListID = this.paramListID = params['id']
        ? Number(params['id'])
        : null;

      if (this.paramListID) {
        // Logic for when 'id' parameter is present
        this.getAllVisuzlizationsAndShow(this.paramListID);
      } else {
        // Logic for when 'id' parameter is not present
        this.listService.getAllUserLists().subscribe(
          (response: any) => {
            this.ownedListsData = this.mapToList(response.owned_lists);

            this.sharedListsData = this.mapToList(response.shared_lists);
            this.showData = true;
          },
          (error: any) => {
            console.error('Error getting all user lists', error);
          }
        );
      }
    });
  }

  showEchelonContentSwitch(echelon: 'owned_lists' | 'shared_lists') {
    setTimeout(() => {
      if (echelon === 'owned_lists') {
        this.showEchelonOwnedLists = !this.showEchelonOwnedLists;
        this.echelonOwnedLists?.nativeElement.scrollIntoView({
          behavior: 'smooth',
        });
      } else if (echelon === 'shared_lists') {
        this.showEcheclonSharedLists = !this.showEcheclonSharedLists;
        this.echelonSharedLists?.nativeElement.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }, 0);
  }

  toggleAddListDialog() {
    this.displayAddListDialog = !this.displayAddListDialog; // Toggle the dialog visibility
  }

  addNewList(data: { name: string; description: string }) {
    const {name, description} = data; // Destructure the name and description from the data object

    this.listService.createList(name, description).subscribe(
      (response) => {
        console.log('List created successfully');

        // Toggle the dialog visibility
        this.toggleAddListDialog();

        // Reload the page to refresh the lists
        window.location.reload();
      },
      (error) => {
        console.error('Error creating list:', error);
        // Handle the error response here (e.g., display an error message to the user)
      }
    );
  }

  toggleRenameListDialog() {
    this.displayRenameListDialog = !this.displayRenameListDialog; // Toggle the dialog visibility
  }

  renameList(listId: number) {
    this.toggleRenameListDialog(); // Open the rename dialog
  }

  removeVisualization(listId: number, visId: number | null, rclId: number | null, customID: string, sqaChartID: number | null): void {
    this.listService
      .removeVisualizationFromList(listId, visId, rclId, customID, sqaChartID)
      .subscribe(
        (response: any) => {
          console.log('Visualization removed from list successfully');
          this.getAllVisuzlizationsAndShow(listId);
        },
        (error: any) => {
          console.error('Error adding vis to list:', error.error.error);
        }
      );
  }

  downloadSVG(svgContent: string, fileName: string) {
    // Convert the SVG string into a Blob
    const blob = new Blob([svgContent], {type: 'image/svg+xml'});

    // Create a temporary URL for the Blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary <a> element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    // Append the <a> element to the document and trigger the click
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }


  async createPNGWithHtml2Canvas(element: HTMLElement): Promise<string> {
    const canvas = await html2canvas(element, {backgroundColor: "white"});
    return canvas.toDataURL("image/png");
  }

  async createPPT() {
    const pptx = new PptxGenJS();

    // Get all `app-chart-wrapper` elements from the DOM
    const chartWrappers = document.querySelectorAll('app-chart-comp-selection-wrapper');

    if (chartWrappers.length === 0) {
      console.error('No app-chart-wrapper elements found in the DOM.');
      return;
    }

    for (const [index, wrapper] of Array.from(chartWrappers).entries()) {
      const slide = pptx.addSlide();

      const rcl = this.visualizations[index]?.rcl_id;
      const chart = this.visualizations[index]?.visualization[0];
      const bottomTextContainer = wrapper.querySelector('.bottom-text-container');
      let chart_id = bottomTextContainer?.lastChild?.textContent;
      const idString = `${chart_id}`;

      if (!(wrapper instanceof HTMLElement)) {
        console.error(`Chart wrapper at index ${index} is not an HTMLElement.`);
        continue;
      }

      // Get the `chart-container` element within the current `app-chart-wrapper`
      const chartContainer = wrapper.querySelector('.chart-container');

      if (!(chartContainer instanceof HTMLElement)) {
        console.error(`Chart container not found within wrapper at index ${index}.`);
        continue;
      }

      try {
        // Generate PNG using html2canvas for the chart container
        const pngDataUrl = await this.createPNGWithHtml2Canvas(chartContainer);

        // Add text and the captured PNG image to the slide
        slide.addText(`Chart ID: ${idString}, from series: ${this.listDetails?.name}`, {
          x: 0.5,
          y: 0.5,
        });
        slide.addImage({path: pngDataUrl, x: 1.6, y: 1, w: 7, h: 4});
      } catch (error) {
        console.error(`Error processing chart container at index ${index}:`, error);
      }
    }

    try {
      // Save the PowerPoint file
      await pptx.writeFile({fileName: `${this.listDetails?.name}.pptx`});
      console.log("Presentation created successfully");
    } catch (fileError) {
      console.error("Error saving presentation file:", fileError);
    }
  }

  drop(event: any) {
    if (!event || event.previousIndex === undefined || event.currentIndex === undefined) {
      console.error("Invalid drag event:", event);
      return;
    }

    moveItemInArray(this.visualizations, event.previousIndex, event.currentIndex);

  }

  toggleDragDrop() {
    this.dragDropActive = !this.dragDropActive;
  }

  saveDragDrop() {

    this.visualizations.forEach((vis, index) => {
      vis.visualization.forEach((chart) => {
        chart.order = index + 1;
      });
    });
    if (this.paramListID !== null) {
      this.listService.saveVisualizationsInListOrder(this.paramListID, this.visualizations).subscribe(
        (response: any) => {
          this.ownedListsData = this.mapToList(response.owned_lists);

          this.sharedListsData = this.mapToList(response.shared_lists);
          this.showData = true;
        },
        (error: any) => {
          console.error('Error saving visualizations in list order', error);
        }
      );
    }
    else {
      console.error('No ListID given to save visualizations list order');
    }


    this.toggleDragDrop();
  }

}
